// SDK assertions may leave the queue unusable; invalid SDK values can also
// surface directly while reading a snapshot. Keep only a fixed public category
// and bounded SDK source locations, without retaining the original error.
let failure = null;
const listeners = new Set();
const reportedKinds = new Set();

function textProperty(value, property, limit) {
  try {
    const text = value && value[property];
    return typeof text === 'string' ? text.slice(0, limit) : '';
  } catch (_) {
    return '';
  }
}

function firestoreStackFrames(stack) {
  return stack.split('\n').map((line) => line.trim().replace(/\\/g, '/')).filter((frame) => {
    if (!/^(?:at\s|[^\s]*@(?:https?:|webpack:|file:))/.test(frame)) return false;
    const sourceLocation = frame.split(/[?#]/, 1)[0];
    return /(?:\/@firebase\/firestore\/|\/firebase\/firestore\/|\/firebase-firestore(?:-compat)?(?:\.|\/)|\/firebase_firestore\.js(?:[:?)\s]|$)|\/static\/js\/vendors-[\w-]*node_modules_firebase-fir-[a-f0-9]{6,}\.chunk\.js(?:[:?)\s]|$))/.test(sourceLocation);
  });
}

function safeDiagnostic(reason, kind) {
  const message = typeof reason === 'string' ? reason.slice(0, 4096) : textProperty(reason, 'message', 4096);
  const version = message.match(/\bFIRESTORE\s*\((\d{1,3}\.\d{1,3}\.\d{1,3})(?:[-+][\w.-]+)?\)/i);
  const frames = [];
  const seen = new Set();
  for (const frame of firestoreStackFrames(textProperty(reason, 'stack', 16384))) {
    const coordinates = frame.match(/:(\d{1,8}):(\d{1,8})\)?\s*$/);
    if (!coordinates) continue;
    let file = null;
    if (/\/static\/js\/vendors-[\w-]*node_modules_firebase-fir-[a-f0-9]{6,}\.chunk\.js(?:[:?)\s]|$)/.test(frame)) {
      file = 'firebase-firestore.chunk.js';
    } else {
      const basename = frame.match(/\/(index(?:\.(?:esm2017|esm|cjs|node\.cjs))?\.js|firebase-firestore(?:-compat)?\.js|firebase_firestore\.js)(?:[:?)\s]|$)/);
      if (basename) file = basename[1];
    }
    const line = Number(coordinates[1]);
    const column = Number(coordinates[2]);
    if (!file || line < 1 || column < 1) continue;
    const key = `${file}:${line}:${column}`;
    if (seen.has(key)) continue;
    seen.add(key);
    frames.push(Object.freeze({ file, line, column }));
    if (frames.length === 5) break;
  }
  return Object.freeze({ kind, sdkVersion: version ? version[1] : null, sdkFrames: Object.freeze(frames) });
}

function classifyFailure(reason) {
  const message = typeof reason === 'string'
    ? reason.slice(0, 4096)
    : textProperty(reason, 'message', 4096);
  const code = textProperty(reason, 'code', 100).replace(/^firestore\//, '');
  if (['permission-denied', 'failed-precondition', 'unavailable', 'deadline-exceeded'].includes(code)) return null;
  const stack = textProperty(reason, 'stack', 16384);
  const sdkStack = firestoreStackFrames(stack).length > 0;
  const fatal = /\bINTERNAL (?:ASSERTION FAILED|UNHANDLED ERROR)\b/i.test(message);
  const sdkFatalMessage = /\bFIRESTORE\s*\(\d+\.\d+\.\d+(?:[-+][\w.-]+)?\)\s*:?\s+INTERNAL (?:ASSERTION FAILED|UNHANDLED ERROR)\b/i.test(message);
  if (fatal && (sdkFatalMessage || sdkStack)) return 'sdk-fatal';
  if (
    textProperty(reason, 'name', 80) === 'TypeError'
    && /Cannot use ['"]in['"] operator to search for ['"]nullValue['"] in null/i.test(message)
    && sdkStack
  ) return 'invalid-sdk-value';
  return null;
}

export function reportFirestoreRuntimeFailure(reason) {
  const kind = classifyFailure(reason);
  if (!kind) return false;
  const firstFailure = failure === null;
  if (firstFailure) failure = Object.freeze({ kind });
  if (!reportedKinds.has(kind)) {
    reportedKinds.add(kind);
    try {
      // At most one bounded diagnostic per category; never log the raw error.
      console.error('[firestore] Live updates and saves may be unavailable. Refresh TutorSpace after copying unfinished text.', safeDiagnostic(reason, kind));
    } catch (_) {}
  }
  if (!firstFailure) return true;
  listeners.forEach((listener) => {
    try { listener(); } catch (_) { /* One observer must not prevent the notice. */ }
  });
  return true;
}

export function getFirestoreRuntimeFailure() {
  return failure;
}

export function subscribeFirestoreRuntimeFailure(listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function __resetFirestoreRuntimeRecoveryForTests() {
  failure = null;
  listeners.clear();
  reportedKinds.clear();
}
